import {
  FillOperationParams,
  ProcessDataFromClipboardParams,
  SendToClipboardParams,
} from "ag-grid-community";
import { useCallback, useEffect, useRef } from "react";
import { DataTableColDef, DataTableRowData } from "./DataTable";
import { useDataTableContext } from "../store/DataTableProvider";
import { CellValue, RowIdentifier } from "@inscopix/ideas-hyperformula";
import { captureException } from "@sentry/react";
import { last } from "lodash";
import { convertCellValueToStaticFormula } from "../store/SideEffects";
import assert from "assert";

/**
 * ID of the HTML element used to identify that rich clipboard data originated
 * in IDEAS
 */
const IDEAS_HTML_ORIGIN = "ideas-html-origin";

/**
 * Name of the HTML element attribute used to store IDEAS formulas on cells in
 * text/html clipboard blobs
 */
const IDEAS_FORMULA_ATTRIBUTE = "data-ideas-formula";

/**
 * Creates a `Blob` storing the rich text representation of the data written to
 * the clipboard.
 * @param options.internalData Cell values that should be used if the paste
 * event target is an IDEAS session.
 * @param options.externalData Cell values that should be used if the paste
 * event target is not an IDEAS session.
 * @returns A `Blob` storing the rich text representation of the clipboard data.
 */
const createRichTextBlob = (options: {
  internalData: string[][];
  externalData: string[][];
}) => {
  const { internalData, externalData } = options;
  const table = document.createElement("table");
  table.id = IDEAS_HTML_ORIGIN;
  const tbody = document.createElement("tbody");
  table.appendChild(tbody);
  internalData.forEach((row, rowIdx) => {
    const tr = document.createElement("tr");
    tbody.appendChild(tr);
    row.forEach((internalCellValue, colIdx) => {
      const externalCellValue = externalData[rowIdx][colIdx];
      const td = document.createElement("td");
      td.setAttribute(IDEAS_FORMULA_ATTRIBUTE, internalCellValue);
      td.innerHTML = externalCellValue;
      tr.appendChild(td);
    });
  });

  const serializedHtml = table.outerHTML;
  return new Blob([serializedHtml], { type: "text/html" });
};

/**
 * Checks if the given values form a linear progression. If they do, it
 * calculates the next value in the sequence.
 * @param values The cell values to check.
 * @param offset The offset to apply to the next value.
 * @returns Whether the values form a linear progression and the next value in
 * the sequence.
 */
const isLinearProgression = (
  values: CellValue[],
  offset: number,
): { isLinearProgression: boolean; nextValue: CellValue } => {
  // Check that there are at least two values
  if (values.length < 2) {
    return {
      isLinearProgression: false,
      nextValue: null,
    };
  }

  // Check that all values are number or strings storing numbers
  const isAllNumeric = values.every((value) => {
    return (
      typeof value === "number" ||
      (typeof value === "string" && !isNaN(Number(value)))
    );
  });

  // Calculate the next value in a sequence of numbers
  if (isAllNumeric) {
    const numbers = values.map((value) => {
      return typeof value === "number" ? value : Number(value as string);
    });

    // Check that the difference between all values is the same
    const diff = numbers[1] - numbers[0];
    for (let i = 2; i < numbers.length; i++) {
      if (numbers[i] - numbers[i - 1] !== diff) {
        return {
          isLinearProgression: false,
          nextValue: null,
        };
      }
    }

    // Calculate the next value in the sequence
    const nextValue = numbers[numbers.length - 1] + diff * offset;
    return {
      isLinearProgression: true,
      nextValue: nextValue,
    };
  }

  // Check that all values are row identifiers
  const isAllRowIdentifiers = values.every((value) => {
    return value instanceof RowIdentifier;
  });

  // Calculate the next value in a sequence of row identifiers
  if (isAllRowIdentifiers) {
    const rowIdentifiers = values as RowIdentifier[];
    const rowIndices = rowIdentifiers.map((value) => value.rowIndex);
    const allSameTableKeys = rowIdentifiers.every((value) => {
      return value.tableKey === rowIdentifiers[0].tableKey;
    });
    const sequence = isLinearProgression(rowIndices, offset);

    if (allSameTableKeys && sequence.isLinearProgression) {
      const nextRowIdentifier = new RowIdentifier(
        rowIdentifiers[0].tableKey,
        sequence.nextValue as number,
      );
      return {
        isLinearProgression: true,
        nextValue: nextRowIdentifier,
      };
    }
  }

  return {
    isLinearProgression: false,
    nextValue: null,
  };
};

/**
 * Hook that defines functions for managing clipboard and fill operations within
 * a data table.
 * @param tableId The ID of the current table.
 */
export const useDataTableClipboard = (tableId: string) => {
  const pasteCellFormula = useDataTableContext((s) => s.pasteCellFormula);
  const richClipboardDataRef = useRef<string[][]>();

  /**
   * Copies the selected cell values to the system clipboard and the store's
   * clipboard state.
   * @param params
   */
  const sendToClipboard = useCallback(
    (params: SendToClipboardParams<DataTableRowData>) => {
      // Multi-range selection is suppressed through props passed to the grid.
      // If there is a selected cell range, we can safely assume it is the only
      // cell range.
      const range = params.api.getCellRanges()?.[0];

      // If no cells are selected, do nothing
      const startRowIndex = range?.startRow?.rowIndex;
      const endRowIndex = range?.endRow?.rowIndex;
      if (
        range === undefined ||
        startRowIndex === undefined ||
        endRowIndex === undefined
      ) {
        return null;
      }

      // Read clipboard values from row data
      const externalClipboardData: string[][] = [];
      const internalClipboardData: string[][] = [];

      for (
        // Start index is not guaranteed to come before end index
        let i = Math.min(startRowIndex, endRowIndex);
        i <= Math.max(startRowIndex, endRowIndex);
        i++
      ) {
        const row = params.api.getDisplayedRowAtIndex(i);
        if (row) {
          const externalClipboardRowData: string[] = [];
          const internalClipboardRowData: string[] = [];
          range.columns.forEach((column) => {
            const colDef = column.getColDef() as DataTableColDef;
            const clipboardValue = colDef.clipboardValueGetter(row);
            if (clipboardValue !== null) {
              externalClipboardRowData.push(clipboardValue.external);
              internalClipboardRowData.push(clipboardValue.internal);
            }
          });
          externalClipboardData.push(externalClipboardRowData);
          internalClipboardData.push(internalClipboardRowData);
        } else {
          const emptyRow = new Array<string>(range.columns.length).fill("");
          externalClipboardData.push(emptyRow);
          internalClipboardData.push(emptyRow);
        }
      }

      // Create a clipboard item with the Blob
      const clipboardItem = new ClipboardItem({
        // Used for pasting plain text
        "text/plain": externalClipboardData
          .map((row) => row.join("\t"))
          .join("\n"),
        // Used for pasting rich text
        "text/html": createRichTextBlob({
          internalData: internalClipboardData,
          externalData: externalClipboardData,
        }),
      });

      // Write the clipboard item to the clipboard
      void navigator.clipboard.write([clipboardItem]).catch(() => {
        captureException("Failed to write to clipboard");
      });
    },
    [],
  );

  // Reads the rich text from the clipboard and determines whether the clipboard
  // data originated from IDEAS.
  useEffect(() => {
    /**
     * Reads the IDEAS cell formulas encoded in rich clipboard data originating
     * from IDEAS.
     * @param ideasOriginTag
     * @returns A 2D array of cell formulas.
     * @throws An error if the clipboard data is malformed.
     */
    const parseRichText = (ideasOriginTag: HTMLElement) => {
      // Get the tbody child element
      const tableBody = ideasOriginTag.firstChild;
      assert(tableBody !== null);

      // Read the cell formulas from rich clipboard data
      return Array.from(tableBody.childNodes).map((row) => {
        return Array.from(row.childNodes).map((cell) => {
          assert(cell instanceof HTMLElement);
          const formula = cell.getAttribute(IDEAS_FORMULA_ATTRIBUTE);
          assert(formula !== null);
          return formula;
        });
      });
    };

    /**
     * Intercepts the paste event and attempts to read cell formulas from
     * clipboard data originating from IDEAS.
     */
    const handlePaste = (e: ClipboardEvent) => {
      const richText = e.clipboardData?.getData("text/html");
      const parser = new DOMParser();
      const doc = parser.parseFromString(richText ?? "", "text/html");
      const ideasOriginTag = doc.getElementById(IDEAS_HTML_ORIGIN);

      // Reset ref data if clipboard data originated from outside IDEAS
      if (ideasOriginTag === null) {
        richClipboardDataRef.current = undefined;
        return;
      }

      // Read the cell formulas from rich clipboard data
      try {
        richClipboardDataRef.current = parseRichText(ideasOriginTag);
      } catch (error) {
        richClipboardDataRef.current = undefined;
      }
    };

    // Add event listener when hook mounts
    document.addEventListener("paste", handlePaste);

    // Remove event listener when hook unmounts
    return () => document.removeEventListener("paste", handlePaste);
  }, []);

  /**
   * Pastes the clipboard data into the target cell range.
   * @param params
   * @returns `null`. This prevents AG Grid from performing its default
   * behavior.
   */
  const processDataFromClipboard = useCallback(
    (params: ProcessDataFromClipboardParams<DataTableRowData>) => {
      // If no cell is focused, do nothing
      const cell = params.api.getFocusedCell();
      if (cell === null) {
        return null;
      }

      // If the row is not displayed, do nothing
      const row = params.api.getDisplayedRowAtIndex(cell.rowIndex);
      if (row === undefined || row.id === undefined) {
        return null;
      }

      // If pasting is disabled for this column, do nothing
      const column = cell.column;
      if (column.isSuppressPaste(row)) {
        return null;
      }

      // Update data store and HyperFormula state
      void pasteCellFormula({
        address: {
          tableId: tableId,
          columnId: cell.column.getColId(),
          rowId: row.id,
        },
        clipboardData:
          richClipboardDataRef.current !== undefined
            ? richClipboardDataRef.current
            : params.data,
      });

      // Suppress AG Grid's default pasting behavior
      return null;
    },
    [pasteCellFormula, tableId],
  );

  /**
   * Checks whether the selected cells form a linear progression. If they do,
   * it continues the sequence. If not, it copies the last selected cell value
   * to all cells.
   * @param params
   * @returns The cell formula to be set on the target cell
   */
  const fillOperation = useCallback(
    (params: FillOperationParams<DataTableRowData>) => {
      const currentCellValue = params.currentCellValue as CellValue;
      // If no cells in selected, do nothing
      if (params.initialValues.length === 0) {
        return currentCellValue;
      }
      // If cells are selected, check if they form a linear progression. If they
      // do, continue the sequence. If not, copy the last selected cell value to
      // all cells.
      else {
        const initialValues = params.initialValues as CellValue[];
        const lastValue = last(initialValues);
        const offset = params.currentIndex + 1;
        const sequence = isLinearProgression(initialValues, offset);
        if (!sequence.isLinearProgression) {
          return convertCellValueToStaticFormula(lastValue ?? null);
        } else {
          return convertCellValueToStaticFormula(sequence.nextValue);
        }
      }
    },
    [],
  );

  return {
    fillOperation,
    processDataFromClipboard,
    sendToClipboard,
  };
};
